import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Space, Button } from 'antd';
import intl from 'react-intl-universal';
import useShoppingPortal from '@/components/Shopping';
import BillPrintDrawer, { IbillPrintDrawerRef } from '@/components/BillPrintDrawer';

import { toggleCreditDrawerVisible } from '@/redux/actions/bill/credit';
import { togglePreSettleDrawerVisible } from '@/redux/actions/bill/preSettle';

import InvoiceBtn from '@/pages/bill/invoice/InvoiceBtn';
import { OrderTypeEnum } from '@/pages/bill/invoice/constant';
import AuthBtn from '@/components/AuthBtn';
import { PERM_CODES } from '@/config';
import AcrossNightRefundDrawer from '../reports/refundAccounts/components/AcrossNightRefundDrawer';
import { SettleDrawerRef, RevSettleDrawerRef, RoomRatePlusDrawerRef } from './interface';
import BillSettle from './settle';
import BillRevSettle from './revSettle';
import BillRoomRatePlus from './roomRatePlus';

import styles from './index.module.scss';

function BillOperates(props) {
  const {
    resvNo,
    selectedRoom,
    billStats,
    orderBillStatus,
    billList,
    orderMasterUid,
    orderMasterInfo,
    masterInfo,
    orderBaseInfo,
    reloadList,
  } = props;
  const { resvStatus, ownerWyuid } = orderBaseInfo;

  const shoppingPortal = useShoppingPortal();
  const settleRef = useRef<SettleDrawerRef>(null);
  const roomRatePlusRef = useRef<RoomRatePlusDrawerRef>(null);
  const revSettleRef = useRef<RevSettleDrawerRef>(null);
  const printRef = useRef<IbillPrintDrawerRef>();
  const acrossNightRef = useRef<any>();

  const [settleDisabled, setSettleDisabled] = useState(false);
  const [revSettleDisabled, setRevSettleDisabled] = useState(false);
  const [preSettleDisabled, setPreSettleDisabled] = useState(false);
  const [creditDisabled, setCreditDisabled] = useState(false);
  const [goodsDisabled, setGoodsDisabled] = useState(false);
  const [invoiceDisabled, setInvoiceDisabled] = useState(false);
  const [roomStatus, setRoomStatus] = useState(null);

  // dynamic button disabled status
  useEffect(() => {
    // 至少有一笔未结的账目
    const billItemSettled = billList.some((bill) => bill.status === 0);

    // 至少有一笔已结的账目
    const billItemNonSettled = billList.some((bill) => bill.status === 1);

    // 订单有房主
    const hasMaster = orderMasterUid !== '';

    // 可结账：账单未结且预订订单、入住订单可直接结账
    const canSettle = orderBillStatus === 0 && ([1, 2].includes(resvStatus) || billItemSettled);

    const canRevSettle = billItemNonSettled; // 可反结账
    const canPreSettle = orderBillStatus === 0; // 可预结算

    // 可入账：账单未结且选择了一个房间
    const canCredit = orderBillStatus === 0 && selectedRoom.roomId !== 0;

    // 可买商品：账单未结且选择了一个房间且订单有房主
    const canClickGoods = orderBillStatus === 0 && selectedRoom.roomId !== 0 && hasMaster;

    // 房间结过账才能开发票
    const canInvoice = billItemNonSettled;

    setSettleDisabled(!canSettle);
    setRevSettleDisabled(!canRevSettle);
    setPreSettleDisabled(!canPreSettle);
    setCreditDisabled(!canCredit);
    setGoodsDisabled(!canClickGoods);
    setRoomStatus(selectedRoom.status);
    setInvoiceDisabled(!canInvoice);
  }, [selectedRoom, orderBillStatus, billList]);

  const settle = () => {
    settleRef.current!.openDrawer({
      resvNo,
    });
  };

  const revSettle = () => {
    revSettleRef.current!.openDrawer({
      resvNo,
    });
  };

  const credit = () => {
    props.toggleCreditDrawerVisible(true);
  };

  const roomRatePlus = () => {
    roomRatePlusRef.current!.openDrawer({
      resvNo,
    });
  };

  const goods = () => {
    // 商品购买弹层
    shoppingPortal.showDrawer({
      resvNo,
      roomStatus, // 4 - 已退房
      ...orderMasterInfo,
      ...masterInfo,
      callback: () => {
        shoppingPortal.closeDrawer();
        reloadList();
      },
      wyuid: ownerWyuid,
    });
  };

  const preSettle = () => {
    props.togglePreSettleDrawerVisible(true);
  };

  return (
    <>
      <AuthBtn>
        <Space className={styles['bill-operates']} align="start" size={16}>
          <Button
            data-authkey={PERM_CODES.SETTLE}
            type="primary"
            disabled={settleDisabled}
            onClick={() => settle()}
          >
            {intl.get('app_checkout')}
          </Button>
          <Button
            data-authkey={PERM_CODES.REVERSE_SETTLE}
            type="primary"
            disabled={revSettleDisabled}
            onClick={() => revSettle()}
          >
            {intl.get('bill_rev_settle_title')}
          </Button>
          <Button
            data-authkey={PERM_CODES.PRE_SETTLE}
            type="primary"
            disabled={preSettleDisabled}
            onClick={() => preSettle()}
          >
            {intl.get('bill_pre_settle_title')}
          </Button>
          <Button
            data-authkey={PERM_CODES.ENTER_ACCOUNT}
            type="primary"
            disabled={creditDisabled}
            onClick={() => credit()}
          >
            {intl.get('bill_credit_account')}
          </Button>
          <Button
            data-authkey={PERM_CODES.EXTRA_ROOM_CHARGE}
            type="primary"
            disabled={creditDisabled}
            onClick={() => roomRatePlus()}
          >
            {intl.get('bill_room_rate_plus_add_room_fee')}
          </Button>
          <Button
            data-authkey={PERM_CODES.GOODS}
            type="primary"
            disabled={goodsDisabled}
            onClick={() => goods()}
          >
            {intl.get('bill_invoice_modal_goods')}
          </Button>
          {/* 这个订单至少存在过1次跨夜审退款，产生了至少1单跨夜审退款订单 */}
          <Button
            type="primary"
            disabled={!billStats.crossNightRefund}
            onClick={() => acrossNightRef.current.openDrawer({ resvNo })}
          >
            {intl.get('bill_across_night_audit_order')}
          </Button>
          <Button
            data-authkey={PERM_CODES.PRINT_BILL_ORDER}
            type="primary"
            onClick={() => printRef.current?.openDrawer({ resvNo })}
          >
            {intl.get('bill_print_bill')}
          </Button>
          <InvoiceBtn
            authkey={PERM_CODES.MAKE_OUT_INVOICE}
            text={intl.get('bill_invoice')}
            type="primary"
            disabled={invoiceDisabled}
            callback={props.reloadList}
            orderInfo={{ orderNo: resvNo, orderType: OrderTypeEnum.reservationOrder }}
          />
        </Space>
      </AuthBtn>
      <BillSettle ref={settleRef} settleCallback={props.reloadList} />
      <BillRoomRatePlus ref={roomRatePlusRef} plusCallback={props.reloadList} />
      <BillRevSettle ref={revSettleRef} revSettleCallback={props.reloadList} />
      <BillPrintDrawer ref={printRef} />

      <AcrossNightRefundDrawer ref={acrossNightRef} />
    </>
  );
}

const mapStateToProps = (state) => {
  const {
    selectedRoom,
    billStats,
    orderBillStatus,
    billList,
    orderMasterUid,
    orderMasterInfo,
    masterInfo,
  } = state.bill;
  const { orderBaseInfo } = state.order;
  return {
    selectedRoom,
    billStats,
    orderBillStatus,
    billList,
    orderMasterUid,
    orderMasterInfo,
    orderBaseInfo,
    masterInfo,
  };
};

export default connect(mapStateToProps, {
  toggleCreditDrawerVisible,
  togglePreSettleDrawerVisible,
})(BillOperates);
